import React, { useEffect, useMemo, useRef, useState } from "react";
import { AtSign, Send, MessageSquare, Trash2 } from "lucide-react";
import { TaskCommentsService } from "./TaskCommentsService";
import { notifyTaskMentionedInComment } from "../../utils/notifications";

function displayUser(u) {
  return (u?.fullName || u?.name || u?.displayName || u?.email || u?.id || "User")
    .toString()
    .trim();
}

function toDateSafe(value) {
  if (!value) return null;
  if (typeof value === "object") {
    if (value?.toDate) return value.toDate();
    if (value?.seconds) return new Date(value.seconds * 1000);
  }
  return null;
}

function formatTime(date) {
  if (!date) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Mention markup: @[Name](uid)
const MENTION_RE = /@\[(.*?)\]\((.*?)\)/g;

function applyTextChangeToMentions(prevText, nextText, mentions) {
  const prev = String(prevText || "");
  const next = String(nextText || "");
  const current = Array.isArray(mentions) ? mentions : [];

  let prefix = 0;
  while (prefix < prev.length && prefix < next.length && prev[prefix] === next[prefix]) prefix += 1;

  let suffix = 0;
  while (
    suffix < prev.length - prefix &&
    suffix < next.length - prefix &&
    prev[prev.length - 1 - suffix] === next[next.length - 1 - suffix]
  ) {
    suffix += 1;
  }

  const prevChangedEnd = prev.length - suffix;
  const delta = next.length - prev.length;

  return current
    .map((mention) => {
      if (mention.end <= prefix) return mention;
      if (mention.start >= prevChangedEnd) {
        return {
          ...mention,
          start: mention.start + delta,
          end: mention.end + delta,
        };
      }
      return null;
    })
    .filter(Boolean);
}

function toMentionMarkup(text, mentions) {
  const source = String(text || "");
  const list = [...(Array.isArray(mentions) ? mentions : [])]
    .filter((mention) => mention && mention.uid && Number.isFinite(mention.start) && Number.isFinite(mention.end))
    .sort((a, b) => b.start - a.start);

  let output = source;
  for (const mention of list) {
    const label = source.slice(mention.start, mention.end) || `@${mention.label || "User"}`;
    output = `${output.slice(0, mention.start)}@[${label.slice(1)}](${mention.uid})${output.slice(mention.end)}`;
  }
  return output;
}

function renderBodyWithMentions(text) {
  const s = String(text || "");
  const parts = [];
  let last = 0;

  for (const match of s.matchAll(MENTION_RE)) {
    const start = match.index ?? 0;
    const full = match[0];
    const name = match[1] || "User";
    const uid = match[2] || "";

    if (start > last) parts.push({ type: "text", value: s.slice(last, start) });
    parts.push({ type: "mention", value: `@${name}`, uid });
    last = start + full.length;
  }

  if (last < s.length) parts.push({ type: "text", value: s.slice(last) });

  return parts.map((p, idx) => {
    if (p.type === "mention") {
      return (
        <span
          key={idx}
          className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-100 text-xs font-medium"
          title={p.uid ? `Mention: ${p.uid}` : "Mention"}
        >
          {p.value}
        </span>
      );
    }
    return <span key={idx}>{p.value}</span>;
  });
}

export default function TaskCommentsPanel({
  companyId,
  taskId,
  taskTitle,
  me,
  usersMap = {},
}) {
  const textareaRef = useRef(null);

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState("");

  const [text, setText] = useState("");
  const [mentions, setMentions] = useState([]);
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // mention UX state
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);

  const myUid = me?.uid || me?.id || null;
  const myRole = String(me?.role || "").trim().toLowerCase();
  const isOwnerOrAdmin = myRole === "owner" || myRole === "admin";

  const staffUsers = useMemo(() => {
    return Object.values(usersMap)
      .map((u) => ({ ...u, uid: u.uid || u.id, id: u.id || u.uid }))
      .filter((u) => Boolean(u?.uid || u?.id))
      .sort((a, b) => displayUser(a).localeCompare(displayUser(b)));
  }, [usersMap]);

  const mentionSuggestions = useMemo(() => {
    if (!mentionOpen) return [];
    const q = (mentionQuery || "").toLowerCase().trim();
    const list = !q
      ? staffUsers
      : staffUsers.filter((u) => displayUser(u).toLowerCase().includes(q));
    return list.slice(0, 6);
  }, [mentionOpen, mentionQuery, staffUsers]);

  useEffect(() => {
    if (!companyId || !taskId) return;

    setLoading(true);
    setErrMsg("");

    let unsub = null;

    try {
      unsub = TaskCommentsService.subscribe(
        companyId,
        taskId,
        (items) => {
          setComments(items);
          setLoading(false);
        },
        (e) => {
          console.error("[TaskCommentsPanel] subscribe error:", e);
          setErrMsg(e?.message || "Failed to load comments.");
          setLoading(false);
        }
      );
    } catch (e) {
      console.error("[TaskCommentsPanel] subscribe threw:", e);
      setErrMsg(e?.message || "Failed to load comments.");
      setLoading(false);
    }

    return () => unsub?.();
  }, [companyId, taskId]);

  const resetMention = () => {
    setMentionOpen(false);
    setMentionStart(-1);
    setMentionQuery("");
    setMentionIndex(0);
  };

  const updateMentionState = (nextText, cursorPos) => {
    const s = String(nextText || "");
    const pos = typeof cursorPos === "number" ? cursorPos : s.length;

    const before = s.slice(0, pos);
    const at = before.lastIndexOf("@");
    if (at < 0) {
      resetMention();
      return;
    }

    const prevChar = at === 0 ? " " : before[at - 1];
    // avoid emails / words like "test@domain"
    if (!/\s/.test(prevChar)) {
      resetMention();
      return;
    }

    const typed = before.slice(at + 1);
    if (typed.includes(" ") || typed.includes("\n") || typed.length > 30) {
      resetMention();
      return;
    }

    setMentionOpen(true);
    setMentionStart(at);
    setMentionQuery(typed);
    setMentionIndex(0);
  };

  const insertMention = (u) => {
    const uid = u?.uid || u?.id;
    if (!uid) return;

    const label = displayUser(u);
    const ta = textareaRef.current;
    const s = String(text || "");

    const cursor = ta?.selectionStart ?? s.length;
    const start = mentionStart >= 0 ? mentionStart : cursor;

    const before = s.slice(0, start);
    const after = s.slice(cursor);

    const token = `@${label} `;
    const next = `${before}${token}${after}`;

    setText(next);
    setMentions((prev) => {
      const adjusted = applyTextChangeToMentions(s, next, prev);
      return [
        ...adjusted,
        {
          uid,
          label,
          start: before.length,
          end: before.length + token.length - 1,
        },
      ].sort((a, b) => a.start - b.start);
    });
    resetMention();

    requestAnimationFrame(() => {
      try {
        const newPos = before.length + token.length;
        ta?.focus();
        ta?.setSelectionRange(newPos, newPos);
      } catch {}
    });
  };

  const onChange = (e) => {
    const next = e.target.value;
    setMentions((prev) => applyTextChangeToMentions(text, next, prev));
    setText(next);
    updateMentionState(next, e.target.selectionStart);
  };

  const onKeyDown = (e) => {
    // Mention dropdown navigation
    if (mentionOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        setMentionIndex((i) => Math.min(i + 1, Math.max(0, mentionSuggestions.length - 1)));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        setMentionIndex((i) => Math.max(0, i - 1));
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        resetMention();
        return;
      }
      if (e.key === "Enter") {
        if (mentionSuggestions[mentionIndex]) {
          e.preventDefault();
          e.stopPropagation();
          insertMention(mentionSuggestions[mentionIndex]);
        }
        return;
      }
    }

    // Optional: Ctrl/Cmd+Enter to send
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      void send(e);
    }
  };

  const send = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();

    setErrMsg("");

    const bodyPlain = String(text || "").trim();
    if (!bodyPlain) return;

    if (!companyId || !taskId) {
      setErrMsg("Missing company/task.");
      return;
    }

    const actorUid = myUid;
    if (!actorUid) {
      setErrMsg("You must be logged in to comment.");
      return;
    }

    const actorName =
      me?.displayName || me?.fullName || me?.name || me?.email || "Someone";

    const body = toMentionMarkup(text, mentions).trim();
    const mentionedUserIds = Array.from(new Set((mentions || []).map((mention) => mention.uid).filter(Boolean)));

    setSending(true);
    try {
      // ✅ RULES-FRIENDLY SCHEMA:
      // text + createdBy (not body + createdByUid)
      const commentId = await TaskCommentsService.addComment(companyId, taskId, {
        companyId,
        entityType: "task",
        entityId: taskId,

        // Keep your mention markup in `text`
        text: body,
        textPlain: bodyPlain,
        mentionedUserIds,

        createdBy: actorUid,
        createdByName: actorName,
        createdByRole: me?.role || "",
      });

      // ✅ notify mentioned users (optional)
      if (mentionedUserIds.length > 0) {
        await notifyTaskMentionedInComment({
          companyId,
          taskId,
          taskTitle: taskTitle || "Task",
          commentId,
          commentText: bodyPlain,
          commenterName: actorName,
          mentionedUserIds,
        });
      }

      setText("");
      setMentions([]);
      resetMention();
    } catch (err) {
      console.error("[TaskCommentsPanel] send failed:", err);
      setErrMsg(err?.message || "Failed to post comment.");
    } finally {
      setSending(false);
    }
  };

  const canDeleteComment = (c) => {
    if (!myUid) return false;
    const author = c?.createdBy || c?.createdByUid || null;
    return Boolean(isOwnerOrAdmin || (author && author === myUid));
  };

  const onDelete = async (e, c) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();

    if (!companyId || !taskId || !c?.id) return;
    if (!canDeleteComment(c)) return;

    const ok = window.confirm("Delete this comment?");
    if (!ok) return;

    setErrMsg("");
    setDeletingId(c.id);

    try {
      await TaskCommentsService.deleteComment(companyId, taskId, c.id);
    } catch (err) {
      console.error("[TaskCommentsPanel] delete failed:", err);
      setErrMsg(err?.message || "Failed to delete comment.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-slate-700" />
          <div className="text-sm font-semibold text-slate-900">Comments</div>
          <span className="text-[11px] text-slate-400">({comments.length})</span>
        </div>

        <div className="text-[11px] text-slate-500 flex items-center gap-1">
          <AtSign className="w-3.5 h-3.5" />
          Type <span className="font-semibold">@</span> to mention
        </div>
      </div>

      {errMsg && (
        <div className="mb-2 px-3 py-2 rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm">
          {errMsg}
        </div>
      )}

      <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-xl bg-slate-50">
        {loading ? (
          <div className="p-3 text-xs text-slate-500">Loading…</div>
        ) : comments.length === 0 ? (
          <div className="p-3 text-xs text-slate-500">No comments yet.</div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {comments.map((c) => {
              const when = formatTime(toDateSafe(c.createdAt));
              const name = c.createdByName || "Someone";
              const authorUid = c.createdBy || c.createdByUid || null;
              const isMe = myUid && authorUid === myUid;

              // Prefer `text` but gracefully render legacy fields too
              const body = c.text || c.body || c.bodyPlain || c.textPlain || "";

              return (
                <li key={c.id} className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-xs font-semibold text-slate-900 truncate">
                          {name}
                          {isMe ? (
                            <span className="ml-1 text-[11px] text-slate-400">(you)</span>
                          ) : null}
                        </div>
                        {when && <div className="text-[11px] text-slate-400">{when}</div>}
                      </div>

                      <div className="mt-1 text-xs text-slate-700 leading-relaxed whitespace-pre-wrap break-words">
                        {renderBodyWithMentions(body)}
                      </div>
                    </div>

                    {canDeleteComment(c) && (
                      <button
                        type="button"
                        onClick={(e) => onDelete(e, c)}
                        disabled={deletingId === c.id}
                        className="shrink-0 inline-flex items-center justify-center p-2 rounded-lg border border-slate-200 bg-white hover:bg-red-50 text-red-600 disabled:opacity-50"
                        title="Delete comment"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-2 relative">
        <textarea
          ref={textareaRef}
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={3}
          placeholder="Write a comment… (use @ to mention someone)"
          className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-white"
          disabled={sending}
        />

        {mentionOpen && mentionSuggestions.length > 0 && (
          <div className="absolute left-2 bottom-12 w-[min(420px,95%)] bg-white border border-slate-200 shadow-lg rounded-xl overflow-hidden z-20">
            <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-200">
              Mention someone…
            </div>
            <ul className="max-h-48 overflow-y-auto">
              {mentionSuggestions.map((u, idx) => {
                const uid = u.uid || u.id;
                const active = idx === mentionIndex;
                return (
                  <li key={uid}>
                    <button
                      type="button"
                      onMouseDown={(ev) => ev.preventDefault()} // keep textarea focus
                      onClick={() => insertMention(u)}
                      className={`w-full text-left px-3 py-2 text-sm ${
                        active ? "bg-blue-50" : "hover:bg-slate-50"
                      }`}
                    >
                      <div className="font-medium text-slate-900">{displayUser(u)}</div>
                      <div className="text-[11px] text-slate-500">{uid}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="mt-2 flex items-center justify-end">
          <button
            type="button"
            onClick={send}
            disabled={sending || !String(text || "").trim()}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {sending ? "Posting…" : "Post Comment"}
          </button>
        </div>

        <div className="mt-1 text-[11px] text-slate-400">
          Tip: Press <span className="font-semibold">Ctrl</span>/<span className="font-semibold">Cmd</span> +{" "}
          <span className="font-semibold">Enter</span> to post.
        </div>
      </div>
    </div>
  );
}
